import React, { useRef } from "react";
import {
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Box,
  Tooltip,
  alpha,
  Typography,
  CardContent,
  useTheme,
} from "@mui/material";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import ScatterPlotRoundedIcon from "@mui/icons-material/ScatterPlotRounded";
import BubbleChartRoundedIcon from "@mui/icons-material/BubbleChartRounded";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import PsychologyIcon from "@mui/icons-material/Psychology"; // NEW
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AddIcon from "@mui/icons-material/Add";
import { CleanInputCard, CleanIconButton } from "./saanvi-styles";

export default function InputBar({
  message,
  setMessage,
  loading,
  deepAnalysis,
  deepDive,
  deeperDive,
  thinkingMode, // NEW
  selectedFiles,
  onSend,
  onStop,
  onKeyDown,
  onRemoveFile,
  onOpenAnalysisModal,
  onOpenFileMenu,
  onThinkingModeToggle,
}) {
  const theme = useTheme();

  // Determine which mode is active
  const activeMode = thinkingMode
    ? "thinking"
    : deepAnalysis
    ? "deep_analysis"
    : deepDive
    ? "deep_dive"
    : deeperDive
    ? "deeper_dive"
    : null;

  return (
    <Stack spacing={1.5}>
      {/* File previews */}
      {selectedFiles.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {selectedFiles.map((file, index) => (
            <Box
              key={index}
              sx={{
                position: "relative",
                borderRadius: "12px",
                overflow: "hidden",
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                background: alpha(theme.palette.background.paper, 0.5),
              }}
            >
              {file.type.startsWith("image/") && file.preview ? (
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    position: "relative",
                  }}
                >
                  <Box
                    component="img"
                    src={file.preview}
                    alt={file.name}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => onRemoveFile(index)}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      background: alpha(theme.palette.background.paper, 0.9),
                      width: 24,
                      height: 24,
                      "&:hover": {
                        background: theme.palette.background.paper,
                      },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    position: "relative",
                  }}
                >
                  <PictureAsPdfIcon
                    sx={{ fontSize: 32, color: "error.main" }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "10px",
                      maxWidth: 60,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      textAlign: "center",
                    }}
                  >
                    {file.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => onRemoveFile(index)}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      background: alpha(theme.palette.background.paper, 0.9),
                      width: 24,
                      height: 24,
                      "&:hover": {
                        background: theme.palette.background.paper,
                      },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              )}
            </Box>
          ))}
        </Stack>
      )}

      {/* Input Card */}
      <CleanInputCard>
        <CardContent sx={{ p: 2.5, "&:last-child": { pb: 1.5 } }}>
          <form onSubmit={onSend}>
            <TextField
              fullWidth
              multiline
              maxRows={6}
              minRows={2}
              placeholder="Ask me anything..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={loading}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: { fontSize: "15px", lineHeight: 1.5 },
                endAdornment: (
                  <InputAdornment position="end">
                    <Stack spacing={1} alignItems="center">
                      {!loading && (
                        <Tooltip title="Press Enter to send, Shift+Enter for new line">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              px: 0.75,
                              py: 0.25,
                              borderRadius: "6px",
                              background: alpha(
                                theme.palette.text.secondary,
                                0.06
                              ),
                              mr: 0.5,
                            }}
                          >
                            <KeyboardReturnIcon
                              sx={{
                                fontSize: 12,
                                color: "text.secondary",
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "10px",
                                color: "text.secondary",
                                fontWeight: 500,
                              }}
                            >
                              Enter
                            </Typography>
                          </Box>
                        </Tooltip>
                      )}
                      {loading ? (
                        <CleanIconButton variant="stop" onClick={onStop}>
                          <StopRoundedIcon sx={{ fontSize: 18 }} />
                        </CleanIconButton>
                      ) : (
                        <CleanIconButton
                          variant="send"
                          type="submit"
                          disabled={
                            !message.trim() && selectedFiles.length === 0
                          }
                        >
                          <SendRoundedIcon sx={{ fontSize: 18 }} />
                        </CleanIconButton>
                      )}
                    </Stack>
                  </InputAdornment>
                ),
              }}
            />
          </form>

          {/* Bottom Actions Row */}
          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 1.5 }}
            flexWrap="wrap"
            alignItems="center"
          >
            {/* File Upload Button */}
            <IconButton
              onClick={onOpenFileMenu}
              disabled={loading}
              size="small"
              sx={{
                borderRadius: "10px",
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                "&:hover": {
                  background: alpha(theme.palette.primary.main, 0.08),
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <AddIcon sx={{ fontSize: 20 }} />
            </IconButton>

            {/* NEW: Thinking Mode Button - SEPARATE */}
            <Box
              onClick={loading ? undefined : onThinkingModeToggle}
              sx={{
                padding: "6px 12px",
                borderRadius: "12px",
                border: `1px solid ${
                  thinkingMode
                    ? alpha(theme.palette.info.main, 0.3)
                    : alpha(theme.palette.divider, 0.3)
                }`,
                background: thinkingMode
                  ? alpha(theme.palette.info.main, 0.1)
                  : "transparent",
                cursor: loading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                transition: "all 0.2s ease",
                opacity: loading ? 0.5 : 1,
                "&:hover": {
                  borderColor: loading ? undefined : theme.palette.info.main,
                  background: loading
                    ? undefined
                    : alpha(theme.palette.info.main, 0.08),
                },
              }}
            >
              <PsychologyIcon
                sx={{
                  fontSize: 16,
                  color: thinkingMode
                    ? theme.palette.info.main
                    : "text.secondary",
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: thinkingMode
                    ? theme.palette.info.main
                    : "text.secondary",
                }}
              >
                Thinking
              </Typography>
            </Box>

            {/* Analysis Mode Button - UPDATED to exclude thinking mode logic */}
            <Box
              onClick={onOpenAnalysisModal}
              sx={{
                padding: "6px 12px",
                borderRadius: "12px",
                border: `1px solid ${
                  deepAnalysis || deepDive || deeperDive
                    ? alpha(theme.palette.primary.main, 0.3)
                    : alpha(theme.palette.divider, 0.3)
                }`,
                background:
                  deepAnalysis || deepDive || deeperDive
                    ? alpha(theme.palette.primary.main, 0.1)
                    : "transparent",
                cursor: loading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                transition: "all 0.2s ease",
                opacity: loading ? 0.5 : 1,
                "&:hover": {
                  borderColor: loading ? undefined : theme.palette.primary.main,
                  background: loading
                    ? undefined
                    : alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              {deepAnalysis ? (
                <ScatterPlotRoundedIcon
                  sx={{ fontSize: 16, color: theme.palette.primary.main }}
                />
              ) : deepDive ? (
                <BubbleChartRoundedIcon
                  sx={{ fontSize: 16, color: theme.palette.primary.main }}
                />
              ) : deeperDive ? (
                <TravelExploreIcon
                  sx={{ fontSize: 16, color: theme.palette.primary.main }}
                />
              ) : (
                <ScatterPlotRoundedIcon sx={{ fontSize: 16 }} />
              )}
              <Typography
                variant="body2"
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color:
                    deepAnalysis || deepDive || deeperDive
                      ? theme.palette.primary.main
                      : "text.primary",
                }}
              >
                {deepAnalysis
                  ? "Deep Analysis"
                  : deepDive
                  ? "Deep Dive"
                  : deeperDive
                  ? "Deeper Dive"
                  : "Analysis"}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </CleanInputCard>
    </Stack>
  );
}
